import { FEATURES } from "@/lib/features";
import { GetTheAppButton } from "@/components/perform/GetTheApp";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CurrencyPicker, guessCurrency } from "@/pages/Membership";
import { LegalFooterLinks } from "@/pages/Legal";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { AvaHiveOrb } from "@/components/ui/AvaHiveOrb";
import { OctonLogo } from "@/components/ui/OctonLogo";
import { useAuth } from "@/context/AuthContext";
import { IMG } from "@/lib/assets";
import { Brain, Mic, Dumbbell, Moon, ArrowRight, Activity, Salad, Flower2, Zap } from "lucide-react";

const features = [
  { icon: Mic, title: "Live Voice Coaching", body: "Talk to OCTON in real time. Low-latency speech-to-speech coaching that adapts mid-set.", span: "md:col-span-7", accent: "#0047FF" },
  { icon: Brain, title: "HNHB Brain", body: "Hebbian deep learning plus Bayesian propagation. OCTON grows a living memory compartment for you.", span: "md:col-span-5", accent: "#CCFF00" },
  { icon: Moon, title: "Nightly Sleep / Replay", body: "Every night OCTON compresses, replays and anti-Hebbian decays, then narrates what it learned.", span: "md:col-span-5", accent: "#FFB340" },
  { icon: Salad, title: "AI Diet Plans", body: "Feed OCTON your vitals and it builds a diet from scratch, tuned to your goals and macros.", span: "md:col-span-4", accent: "#CCFF00" },
  { icon: Flower2, title: "Mind & Flow", body: "Cinematic yoga, mobility and breathwork for recovery and calm focus.", span: "md:col-span-3", accent: "#0047FF" },
];

const programs = [
  { img: IMG.strength, title: "Strength", tag: "Barbell power" },
  { img: IMG.conditioning, title: "Conditioning", tag: "Engine builder" },
  ...(FEATURES.padel ? [{ img: IMG.padelHero, title: "Padel Academy", tag: "Court IQ & shots" }] : []),
  { img: IMG.yoga, title: "Yoga", tag: "Mobility & breath" },
  { img: IMG.nutrition, title: "Nutrition", tag: "Fuel the fleet" },
];

const SYM = { eur: "€", gbp: "£", usd: "$" };

function Pricing({ navigate }) {
  const [plans, setPlans] = useState([]);
  const [cur, setCur] = useState(guessCurrency);
  useEffect(() => { api.get("/api/membership/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  if (!plans.length) return null;
  return (
    <section className="px-6 md:px-12 pb-20" id="pricing" data-testid="landing-pricing">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <span className="mono-label text-acid">membership</span>
          <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mt-2">One plan for you. One for your club.</h2>
          <p className="text-[#8A91A6] mt-2">Start with a free 7-day taster, no card needed. Cancel any time.</p>
        </div>
        <CurrencyPicker value={cur} onChange={setCur} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
        {plans.map((p) => (
          <div key={p.lookup_key} className={`surface rounded-xl p-7 flex flex-col ${p.popular ? "border-acid" : ""}`}>
            <h3 className="font-heading font-black text-2xl tracking-tight">{p.name}</h3>
            <p className="text-[#8A91A6] text-sm mt-1">{p.tagline}</p>
            <p className="my-5"><span className="font-heading font-black text-4xl">{SYM[cur]}{p.prices?.[cur] ?? p.price}</span>
              <span className="text-[#8A91A6] text-sm">/month</span></p>
            <ul className="space-y-2 text-sm text-white/85 flex-1 mb-6">{p.features.map((f) => <li key={f}>· {f}</li>)}</ul>
            <Button onClick={() => navigate("/login?mode=register")}
              className={`rounded-full h-11 ${p.popular ? "bg-acid text-black hover:bg-[#AACC00]" : "bg-white text-black hover:bg-acid"}`}>
              {p.club ? "Set up your club" : "Start free taster"}
            </Button>
          </div>
        ))}
      </div>
      <p className="text-[#565C6F] text-xs mt-4">Prices include VAT where applicable. Billed monthly in your chosen currency.</p>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen text-white overflow-x-hidden">
      <header className="glass sticky top-0 z-30 px-6 md:px-12 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5 group" data-testid="logo-home">
          <OctonLogo size={34} />
          <span className="font-heading font-black tracking-tight text-lg group-hover:text-acid transition-colors">OCTON GYM</span>
        </button>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/help")} className="text-sm text-[#8A91A6] hover:text-white transition-colors hidden sm:block" data-testid="nav-help">Help</button>
          {user ? (
            <>
              <span className="text-sm text-[#8A91A6] hidden sm:inline" data-testid="nav-user-greeting">
                Logged in as <span className="text-white font-medium">{user.name?.split(" ")[0]}</span>
              </span>
              <div className="w-8 h-8 rounded-full bg-neural/20 border border-neural/40 flex items-center justify-center text-sm font-bold" data-testid="nav-user-avatar" title={user.name}>
                {user.name?.[0]?.toUpperCase() || "U"}
              </div>
              <Button onClick={() => navigate("/app")} data-testid="nav-dashboard"
                className="bg-acid text-black hover:bg-[#AACC00] rounded-full font-medium">Dashboard</Button>
              <button onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout"
                className="text-sm text-[#8A91A6] hover:text-white transition-colors">Sign out</button>
            </>
          ) : (
            <>
              <GetTheAppButton className="hidden sm:inline-flex items-center gap-1.5 text-sm text-acid hover:underline" />
              <button onClick={() => navigate("/login")} className="text-sm text-[#8A91A6] hover:text-white transition-colors" data-testid="nav-login">Sign in</button>
              <Button onClick={() => navigate("/login?mode=register")} data-testid="nav-getstarted"
                className="bg-acid text-black hover:bg-[#AACC00] rounded-full font-medium">Get started</Button>
            </>
          )}
        </div>
      </header>

      {/* Cinematic hero */}
      <section className="relative min-h-[88vh] flex items-center px-6 md:px-12 py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMG.landingHero} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#050505 0%,rgba(5,5,5,0.6) 32%,rgba(5,5,5,0.15) 68%,transparent)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#050505,transparent 45%)" }} />
        </div>
        <div className="relative z-10 grid md:grid-cols-12 gap-10 items-center w-full">
          <div className="md:col-span-7 space-y-7">
            <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="mono-label inline-flex items-center gap-2 text-acid">
              <Activity className="w-3.5 h-3.5" /> Part of the OCTON fleet
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-7xl leading-[0.98]">
              Your trainer is a<br /><span className="text-acid" style={{ textShadow: "0 0 40px rgba(204,255,0,0.5)" }}>living brain.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
              className="text-[#B7BECB] text-base md:text-lg max-w-xl leading-relaxed">
              OCTON GYM pairs you with OCTON AI personal trainer, grounded in Hebbian deep
              learning and Bayesian propagation. It learns how you train every session, reviews it overnight, and comes
              back sharper. Training, nutrition, mobility and mindset, one living brain.
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="flex flex-wrap gap-3">
              <Button onClick={() => navigate("/login?mode=register")} data-testid="hero-cta"
                className="bg-acid text-black hover:bg-[#AACC00] rounded-full font-medium h-12 px-7 text-base">
                Start free taster <ArrowRight className="w-4 h-4" />
              </Button>
              <Button onClick={() => navigate("/login")} variant="outline"
                className="rounded-full h-12 px-7 border-[#343A4A] bg-black/40 hover:bg-white/5 text-base">
                I have an account
              </Button>
              <Button onClick={() => navigate("/help")} variant="ghost"
                className="rounded-full h-12 px-7 hover:bg-white/5 text-base" data-testid="hero-demo">
                Book a demo
              </Button>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.32 }}
              className="mono-label text-[#8A91A6]" data-testid="hero-taster-note">
              7-day free taster · no card required · train with OCTON today
            </motion.p>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.7 }}
            className="md:col-span-5 hidden md:flex justify-center">
            <AvaHiveOrb status="idle" size={220} predictionError={0.3} onClick={() => navigate("/login")} />
          </motion.div>
        </div>
      </section>

      {/* Programs showcase */}
      <section className="px-6 md:px-12 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="mono-label text-acid">the programmes</span>
            <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mt-2">Depth in every discipline.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {programs.map((p, i) => (
            <motion.button key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.06 }} onClick={() => navigate("/login")}
              className="relative rounded-xl overflow-hidden aspect-[3/4] group">
              <img src={p.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#050505 10%,transparent 70%)" }} />
              <div className="absolute bottom-0 left-0 p-4 text-left">
                <p className="mono-label text-acid">{p.tag}</p>
                <p className="font-heading font-bold text-xl tracking-tight">{p.title}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Bento features */}
      <section className="px-6 md:px-12 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className={`surface rounded-lg p-8 ${f.span} hover:border-[#343A4A] transition-colors group`}>
              <div className="w-11 h-11 rounded-md flex items-center justify-center mb-5"
                style={{ background: `${f.accent}18`, border: `1px solid ${f.accent}44` }}>
                <f.icon className="w-5 h-5" style={{ color: f.accent }} />
              </div>
              <h3 className="font-heading font-bold text-xl tracking-tight mb-2">{f.title}</h3>
              <p className="text-[#8A91A6] text-sm leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Pricing navigate={navigate} />

      <section className="px-6 md:px-12 pb-24">
        <div className="relative rounded-2xl overflow-hidden">
          <img src={IMG.membership} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative p-10 md:p-20 text-center">
            <Zap className="w-8 h-8 text-acid mx-auto mb-4" />
            <h2 className="font-heading font-black text-3xl md:text-5xl tracking-tight mb-4">Every rep feeds the brain.</h2>
            <p className="text-[#B7BECB] max-w-xl mx-auto mb-7">
              Salience-gated encoding, nightly replay, Bayesian insights held as probabilities, never certainties.
            </p>
            <Button onClick={() => navigate("/login?mode=register")} data-testid="cta-bottom"
              className="bg-acid text-black hover:bg-[#AACC00] rounded-full font-medium h-12 px-8">
              Boot up your compartment <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="px-6 md:px-12 py-8 border-t border-[#1E222A] flex flex-col md:flex-row items-center justify-between gap-3 text-[#565C6F] text-sm">
        <div className="flex items-center gap-2"><OctonLogo size={26} glow={false} /><span className="font-heading font-bold text-white">OCTON GYM</span></div>
        <LegalFooterLinks />
        <span className="mono-label">© {new Date().getFullYear()} OCTON Gym</span>
      </footer>
    </div>
  );
}
